import {
  Button,
  Card,
  Divider,
  Form,
  Input,
  Radio,
  Space,
} from "antd";
import { useEffect, useState } from "react";
import { responseNotification } from "../../utils/notify";
import SubmitResetBtn from "../common/SubmitBtn";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import React from "react";

const QuickUpdate = ({ visibleData, onCloseMethod }: any) => {
  const { type, token } = useSelector((state) => (state as any)?.authReducer);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [discountType, setDiscountType] = useState<any>({});

  /* ------------------------------------------------------------------
        ⭐ SELLER PRICE AUTO CALCULATION (with correct Form.List update)
  ------------------------------------------------------------------- */
  const calculateSellerPrice = (index: number) => {
    const variations = [...(form.getFieldValue("variations") || [])];

    const mrp = parseFloat(variations[index]?.mrp) || 0;
    const flat = parseFloat(variations[index]?.flat) || 0;
    const percent = parseFloat(variations[index]?.percentage) || 0;

    let final = mrp;

    if (discountType[index] === "disFlat") {
      final = mrp - flat;
    } else if (discountType[index] === "disPercentage") {
      final = mrp - (mrp * percent) / 100;
    }

    if (final < 0) final = 0;

    variations[index].sellerPrice = final.toFixed(2);

    form.setFieldsValue({ variations }); // <-- FIXED
  };

  /* ------------------------------------------------------------------ */

  const onSubmit = async (data: any) => {
    setLoading(true);

    const readyData = {
      id: visibleData?.id,
      barCode: visibleData?.barCode,
      variations: data.variations.map((v: any, i: number) => ({
        variationName: v.variationName,
        variationId: v.variationId,
        barCode: v.barCode,
        maximumOrderLimit: v.maximumOrderLimit
          ? parseInt(v.maximumOrderLimit)
          : undefined,
        price: {
          mrp: parseFloat(v.mrp),
          tp: parseFloat(v.tp),
          discountedPrice: parseFloat(v.sellerPrice), // ⭐ REQUIRED
        },
        productDiscount: {
          flat: parseFloat(v.flat) || 0,
          percentage: parseFloat(v.percentage) || 0,
        },
        stock: parseFloat(v.stock) || 0,
      })),
    };

    await fetch(`${process.env.REACT_APP_CATALOG_WRITER_API}/product`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...readyData, type }),
    })
      .then((res) => res.json())
      .then((res) => {
        setLoading(false);

        if (res.statusCode === 200) {
          responseNotification("Product Updated Successfully", "success");
          onCloseMethod && onCloseMethod();
        } else {
          responseNotification(res.message || "Something Wrong", "warning");
        }
      })
      .catch((err) => {
        setLoading(false);
        responseNotification("Internal server error", "error");
      });
  };

  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (visibleData?.variations) {
      const dTypes: any = {};

      visibleData.variations.forEach((v: any, i: number) => {
        dTypes[i] = null;
      });

      setDiscountType(dTypes);

      form.setFieldsValue({
        variations: visibleData.variations.map((v: any) => ({
          barCode: v.barCode,
          variationName: v.variationName,
          mrp: v.price?.mrp,
          tp: v.price?.tp,
          stock: v.stock,
          sellerPrice: v.price?.mrp, // ⭐ default = MRP
          flat: v.productDiscount?.flat || 0,
          percentage: v.productDiscount?.percentage || 0,
        })),
      });
    }
  }, [visibleData]);

  /* ------------------------------------------------------------------ */

  return (
    <>
      <Form form={form} onFinish={onSubmit} layout="vertical">
        <Form.List name="variations">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }: any) => (
                <Card
                  key={key}
                  title={visibleData?.variations?.[name]?.variationName}
                  className="mb-4"
                  extra={
                    <Button danger type="text" onClick={() => remove(name)}>
                      <CloseOutlined />
                    </Button>
                  }
                >
                  <Space wrap align="center">
                    <Form.Item
                      {...restField}
                      name={[name, "barCode"]}
                      label="Barcode"
                      style={{ width: 120 }}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "variationName"]}
                      label="Variation"
                      rules={[{ required: true }]}
                      style={{ width: 120 }}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "mrp"]}
                      label="MRP"
                      rules={[{ required: true }]}
                      style={{ width: 90 }}
                    >
                      <Input onChange={() => calculateSellerPrice(name)} />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "tp"]}
                      label="TP"
                      style={{ width: 90 }}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "stock"]}
                      label="Stock"
                      rules={[{ required: true }]}
                      style={{ width: 90 }}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "sellerPrice"]}
                      label="Seller Price"
                      rules={[{ required: true }]}
                      style={{ width: 110 }}
                    >
                      <Input readOnly />
                    </Form.Item>
                  </Space>

                  {/* DISCOUNT SECTION */}
                  <Space wrap align="center" className="mt-3">
                    <Radio.Group
                      value={discountType[name]}
                      onChange={(e) => {
                        setDiscountType({
                          ...discountType,
                          [name]: e.target.value,
                        });
                        calculateSellerPrice(name);
                      }}
                    >
                      <Radio value={null}>None</Radio>
                      <Radio value="disFlat">Flat</Radio>
                      <Radio value="disPercentage">Percentage</Radio>
                    </Radio.Group>

                    {discountType[name] === "disFlat" && (
                      <Form.Item
                        {...restField}
                        name={[name, "flat"]}
                        style={{ width: 120 }}
                      >
                        <Input onChange={() => calculateSellerPrice(name)} />
                      </Form.Item>
                    )}

                    {discountType[name] === "disPercentage" && (
                      <Form.Item
                        {...restField}
                        name={[name, "percentage"]}
                        style={{ width: 120 }}
                      >
                        <Input onChange={() => calculateSellerPrice(name)} />
                      </Form.Item>
                    )}
                  </Space>
                </Card>
              ))}

              <Button
                type="primary"
                danger
                icon={<PlusOutlined />}
                onClick={() => add()}
              >
                Add More
              </Button>
            </>
          )}
        </Form.List>

        <Divider />

        <SubmitResetBtn loading={loading} />
      </Form>
    </>
  );
};

export default QuickUpdate;
